import { Form, Table } from 'react-bootstrap';

import { BotaoAcaoLinha } from '../../components/BotaoAcaoLinha';
import type React from 'react';

import type {
  HonorarioCumprimentoItem,
  HonorarioSentencaItem,
  OficioDemaisItem,
  OficioRequisitorioItem,
} from '../relatorio-civel-page.models';

type ItemSimples = HonorarioSentencaItem | HonorarioCumprimentoItem | OficioRequisitorioItem | OficioDemaisItem;

type UseListasSimplesParams = {
  apenasNumeros: (value: string) => string;
  isViewMode: boolean;
};

export const useListasSimples = (
  { apenasNumeros, isViewMode }: UseListasSimplesParams,
): {
  renderTabelaProcessosSimples: (
    processos: ItemSimples[],
    onRemover: (id: string) => void,
    editandoId?: string | null,
    valorEdicao?: string,
    onValorEdicaoChange?: (value: string) => void,
    onIniciarEdicao?: (item: ItemSimples) => void,
    onSalvarEdicao?: (id: string) => void,
    onCancelarEdicao?: () => void,
  ) => React.ReactNode;
} => {
  const renderTabelaProcessosSimples = createRenderTabelaProcessosSimples({ apenasNumeros, isViewMode });

  return { renderTabelaProcessosSimples };
};

export function createRenderTabelaProcessosSimples({
  apenasNumeros,
  isViewMode,
}: UseListasSimplesParams): (
  processos: ItemSimples[],
  onRemover: (id: string) => void,
  editandoId?: string | null,
  valorEdicao?: string,
  onValorEdicaoChange?: (value: string) => void,
  onIniciarEdicao?: (item: ItemSimples) => void,
  onSalvarEdicao?: (id: string) => void,
  onCancelarEdicao?: () => void,
) => React.ReactNode {
  return (
    processos,
    onRemover,
    editandoId,
    valorEdicao,
    onValorEdicaoChange,
    onIniciarEdicao,
    onSalvarEdicao,
    onCancelarEdicao,
  ) => {
    if (processos.length === 0) {
      return (
        <div className="text-center text-muted py-4 border rounded">
          <i className="bi bi-inbox fs-1"></i>
          <p className="mb-0 mt-2">Nenhum processo adicionado ainda.</p>
          <small>Adicione os números dos processos acima.</small>
        </div>
      );
    }

    return (
      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th style={{ width: '50px' }}>#</th>
            <th>Número do Processo</th>
            {!isViewMode && <th style={{ width: '200px' }}>Ações</th>}
          </tr>
        </thead>
        <tbody>
          {processos.map((processo, index) => {
            const estaEditando = editandoId === processo.id;

            return (
              <tr key={processo.id}>
                <td>{index + 1}</td>
                <td>
                  {estaEditando && valorEdicao !== undefined && onValorEdicaoChange && !isViewMode ? (
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Ex: 000000.000.000"
                      value={valorEdicao}
                      onChange={(e) => onValorEdicaoChange(apenasNumeros(e.target.value))}
                      onKeyDown={(e) => {
                        if (
                          e.key.length === 1 &&
                          !/[0-9]/.test(e.key) &&
                          e.key !== 'Enter' &&
                          e.key !== 'Backspace' &&
                          e.key !== 'Delete' &&
                          e.key !== 'Tab'
                        ) {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px' }}
                    />
                  ) : (
                    processo.numeroProcesso
                  )}
                </td>
                {!isViewMode && (
                  <td>
                    {estaEditando && onSalvarEdicao && onCancelarEdicao ? (
                      <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                        <BotaoAcaoLinha tipo="salvar" onClick={() => onSalvarEdicao(processo.id)} />
                        <BotaoAcaoLinha tipo="cancelar" onClick={onCancelarEdicao} />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                        {onIniciarEdicao && (
                          <BotaoAcaoLinha tipo="editar" onClick={() => onIniciarEdicao(processo)} />
                        )}
                        <BotaoAcaoLinha tipo="excluir" onClick={() => onRemover(processo.id)} />
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };
}

